/**
 * Tiny key/value cache that survives browser refresh AND native app
 * relaunches. Uses @capacitor/preferences on native (sandboxed, faster,
 * not subject to Safari's 7-day clearing for "non-essential" storage),
 * falls back to localStorage on web.
 *
 * Async API so the native and web paths share a single shape. Values
 * are JSON-encoded.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';

const NS = 'dashalign:';

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

export async function persistentGet<T>(key: string): Promise<T | null> {
  const fullKey = NS + key;
  try {
    if (isNative()) {
      const { value } = await Preferences.get({ key: fullKey });
      if (value == null) return null;
      return JSON.parse(value) as T;
    }
    if (typeof localStorage !== 'undefined') {
      const raw = localStorage.getItem(fullKey);
      if (raw == null) return null;
      return JSON.parse(raw) as T;
    }
  } catch {
    // Corrupt value or storage unavailable; treat as cache miss.
  }
  return null;
}

export async function persistentSet<T>(key: string, value: T): Promise<void> {
  const fullKey = NS + key;
  const encoded = JSON.stringify(value);
  try {
    if (isNative()) {
      await Preferences.set({ key: fullKey, value: encoded });
      return;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(fullKey, encoded);
    }
  } catch {
    // Quota exceeded or storage disabled — silent. The next fetch
    // pulls from network.
  }
}

export async function persistentRemove(key: string): Promise<void> {
  const fullKey = NS + key;
  try {
    if (isNative()) {
      await Preferences.remove({ key: fullKey });
      return;
    }
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(fullKey);
    }
  } catch {
    // ignore
  }
}
